import { GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery } from '@/__generated__/graphql';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import { FormFooter } from '@/components/basicComponents/formFooter';
import Input from '@/components/basicComponents/input';
import Select, { ISelectValue } from '@/components/basicComponents/select';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { useErrorMessages } from '@/hooks/i18n';
import { useResponsive } from '@/hooks/use-responsive';
import { MainWrap, PaddingWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import { processExpectedAttachments } from '@trace-8/trace8-schema';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { openDocumentationAttachmentFile } from '../templateComponents/rowComponents/attachmentsComponent';
import { IDocumentationAttachment, IRenderTemplateRow } from '../templateComponents/types';

interface IFromValues {
  type: string;
  description: string;
  associatedContact: string;
}

const defaultFormValues: IFromValues = {
  type: '',
  description: '',
  associatedContact: ''
};

export enum AttachmentSubTypesEnum {
  governmentIssuedId = 'Government Issued ID',
  w9 = 'W9',
  legalEntityDocumentation = 'Legal Entity Formation Document',
  trustDocumentation = 'Trust Formation Document',
  authorizedAgents = 'Authorized Agents',
  bankInformation = 'Bank Information',
  beneficialOwners = 'Beneficial Owners List/Documentation',
  interestedParties = 'Interested Parties Contact Sheet',
  other = 'Other'
}

const fileExtensions = ['PDF', 'DOC', 'DOCX', 'XLS', 'XLSX', 'CSV', 'JPEG', 'PNG', 'GIF', 'JPG'];
const contactAttachmentsTypes: ISelectValue[] = [
  { label: AttachmentSubTypesEnum.governmentIssuedId, value: 'governmentIssuedId' },
  { label: AttachmentSubTypesEnum.trustDocumentation, value: 'trustDocumentation' },
  { label: AttachmentSubTypesEnum.legalEntityDocumentation, value: 'legalEntityDocumentation' },
  { label: AttachmentSubTypesEnum.w9, value: 'w9' }
];
const generalAttachmentsTypes: ISelectValue[] = [
  { label: AttachmentSubTypesEnum.authorizedAgents, value: 'authorizedAgents' },
  { label: AttachmentSubTypesEnum.beneficialOwners, value: 'beneficialOwners' },
  { label: AttachmentSubTypesEnum.bankInformation, value: 'bankInformation' },
  { label: AttachmentSubTypesEnum.interestedParties, value: 'interestedParties' },
  { label: AttachmentSubTypesEnum.other, value: 'other' }
];
const allAttachmentTypes = Object.entries(AttachmentSubTypesEnum).map(([key, value]) => ({
  label: value,
  value: key
}));

interface AddAttachmentFileModalPageProps {
  row: IRenderTemplateRow;
  entityId: string;
  pageName?: string;
  contacts: GenerateSignaturePacketPageLegalEntityBankAccountsAndContactsQuery['legalEntity']['contacts'];
  currentAttachment: IDocumentationAttachment | null;
  formData: Record<string, any>;
  currentAttachmentSectionInfo: { sectionName: string; expectedAttachmentName: string; contactId: string };
  getFieldValue: (name: string) => string[];
  saveFieldValue: (name: string, value: any) => void;
  unregisterField: (name: string) => void;
  closeModalPage: () => void;
}

export const AddAttachmentFileModalPage = ({
  row,
  entityId,
  pageName = '',
  contacts = [],
  currentAttachment,
  formData,
  currentAttachmentSectionInfo,
  getFieldValue,
  saveFieldValue,
  unregisterField,
  closeModalPage
}: AddAttachmentFileModalPageProps) => {
  const { isMobile } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();
  const {
    control,
    formState: { errors },
    reset,
    setValue,
    handleSubmit
  } = useForm<IFromValues>({ defaultValues: defaultFormValues });
  const [schema, attachmentSections] = processExpectedAttachments(formData, contacts, row?.config);

  const [drag, setDrag] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: '' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [uploadFilesLoading, setUploadFilesLoading] = useState(false);
  const [attachmentTypesOptions, setAttachmentTypesOptions] = useState<ISelectValue[]>([]);
  const [associatedContactOptions, setAssociatedContactOptions] = useState<ISelectValue[]>([]);
  const [showAssociatedContactSelect, setShowAssociatedContactSelect] = useState(false);

  useEffect(() => {
    if (!currentAttachment) return;
    const { type, description, associatedContact } = currentAttachment.metadata;
    reset((defaultValues) => ({ ...defaultValues, type: type || '', description, associatedContact }));
  }, [currentAttachment]);

  useEffect(() => {
    const { sectionName, expectedAttachmentName, contactId } = currentAttachmentSectionInfo;

    if (!sectionName && !expectedAttachmentName) {
      setAttachmentTypesOptions(allAttachmentTypes);
      return;
    }

    if (currentAttachment) {
      if (sectionName === 'general') {
        setAttachmentTypesOptions(generalAttachmentsTypes);
      } else {
        setAttachmentTypesOptions(contactAttachmentsTypes);
      }
      return;
    }

    if (expectedAttachmentName) {
      setValue('type', expectedAttachmentName);
    }

    if (sectionName === 'general') {
      setAttachmentTypesOptions(generalAttachmentsTypes);
      return;
    }

    setValue('associatedContact', contactId || '');
    setAttachmentTypesOptions(contactAttachmentsTypes);
  }, [currentAttachmentSectionInfo, currentAttachment]);

  useEffect(() => {
    if (!contacts.length) return;

    const allowedContactIds = attachmentSections.flatMap((attachmentSection) => attachmentSection?.contactId).filter(Boolean);
    const filteredContacts = contacts.filter((contact) => allowedContactIds.includes(contact.id));

    setAssociatedContactOptions(
      filteredContacts.map((contact) => ({
        label: contact.name,
        value: contact.id
      }))
    );
  }, [contacts]);

  useEffect(() => {
    if (currentAttachment) {
      const isContactAttachmentsType = contactAttachmentsTypes.some((item) => item.value === currentAttachment?.metadata.type);
      setShowAssociatedContactSelect(isContactAttachmentsType);
    } else if (currentAttachmentSectionInfo?.expectedAttachmentName) {
      const isContactAttachmentsType = contactAttachmentsTypes.some((item) => item.value === currentAttachmentSectionInfo?.expectedAttachmentName);
      setShowAssociatedContactSelect(isContactAttachmentsType);
    }
  }, [currentAttachment]);

  const allowFileExtensions = (files: FileList | null) => {
    return Array.from(files || []).filter((file: File) => {
      const fileExt = file.name.split('.').pop()?.toLowerCase();
      if ([...fileExtensions.map((item) => item.toLowerCase())].includes(fileExt || '')) {
        return file;
      }
    });
  };

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;

    setDrag(false);
    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    setFile(allowFileExtensions(uploadFiles)[0]);
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (!allowFileExtensions(uploadFiles).length) {
      setModalWindow({ isOpen: true, type: 'not-supported' });
      return;
    }
    setFile(allowFileExtensions(uploadFiles)[0]);
  };

  const saveAttachment = async (file: File, data: IFromValues) => {
    try {
      setUploadFilesLoading(true);
      setErrorMessage(null);
      const { type, description, associatedContact } = data;

      const formData = new FormData();
      formData.append('file', file);
      formData.append('metadata', JSON.stringify({ type, description, associatedContact }));

      await axios.put(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments/${encodeURIComponent(file.name)}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (getFieldValue && saveFieldValue) {
        const isContactAttachmentsType = contactAttachmentsTypes.some((item) => item.value === type);
        if (associatedContact && isContactAttachmentsType) {
          const contact = contacts.find((c) => c.id === associatedContact);
          if (!contact) {
            setErrorMessage(defaultErrorMessage);
            return;
          }

          const section = attachmentSections.find((s) => s?.contactId === contact.id);
          if (!section) {
            setErrorMessage(defaultErrorMessage);
            return;
          }

          const sectionKey = `${pageName}.${row.name}.${section.name}`;
          const attachmentNames = getFieldValue(`${sectionKey}.${type}`) || [];
          saveFieldValue(`${sectionKey}.type`, 'contact');
          saveFieldValue(`${sectionKey}.contactId`, contact.id);
          saveFieldValue(`${sectionKey}.${type}`, [...attachmentNames, file.name]);
        } else {
          const attachmentNames = getFieldValue(`${pageName}.${row.name}.general.${type}`);
          saveFieldValue(`${pageName}.${row.name}.general.type`, 'general');
          saveFieldValue(`${pageName}.${row.name}.general.${type}`, [...(attachmentNames || []), file.name]);
        }
      }

      closeModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setUploadFilesLoading(false);
    }
  };

  const updateAttachmentMetadata = async (data: IFromValues) => {
    try {
      setUploadFilesLoading(true);
      setErrorMessage(null);
      const { type, description, associatedContact } = data;

      await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/legal-entity-attachment/legal-entity/${entityId}/attachments/${encodeURIComponent(
          currentAttachment.name
        )}/metadata`,
        {
          metadata: { type, description, associatedContact }
        },
        {
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      if (getFieldValue && saveFieldValue) {
        const allAttachments: any = getFieldValue(`${pageName}.${row.name}`) || {};
        const isFileAlreadyExists = Object.values(allAttachments).some((section: any) =>
          Object.entries(section)
            .filter(([key]) => key !== 'type' && key !== 'contactId')
            .some(([, files]) => Array.isArray(files) && files.includes(currentAttachment.name))
        );

        const isContactAttachmentsType = contactAttachmentsTypes.some((item) => item.value === type);
        if (associatedContact && isContactAttachmentsType) {
          const contact = contacts.find((c) => c.id === associatedContact);
          if (!contact) {
            setErrorMessage(defaultErrorMessage);
            return;
          }

          const section = attachmentSections.find((s) => s?.contactId === contact.id);
          if (!section) {
            setErrorMessage(defaultErrorMessage);
            return;
          }

          const sectionKey = `${pageName}.${row.name}.${section.name}`;
          const typeKey = `${sectionKey}.type`;

          saveFieldValue(typeKey, 'contact');
          saveFieldValue(`${sectionKey}.contactId`, contact.id);

          if (isFileAlreadyExists && currentAttachmentSectionInfo?.expectedAttachmentName) {
            const prevAttachmentKey = `${sectionKey}.${currentAttachmentSectionInfo.expectedAttachmentName}`;
            const prevFilesArray = getFieldValue(prevAttachmentKey) || [];
            const updatedPrevFilesArray = prevFilesArray.filter((fileName: string) => fileName !== currentAttachment.name);
            if (!updatedPrevFilesArray?.length) {
              unregisterField(prevAttachmentKey);
            } else {
              saveFieldValue(prevAttachmentKey, updatedPrevFilesArray);
            }
          }

          const attachmentNames = getFieldValue(`${sectionKey}.${type}`) || [];
          if (!attachmentNames.includes(currentAttachment.name)) {
            saveFieldValue(`${sectionKey}.${type}`, [...attachmentNames, currentAttachment.name]);
          }
        } else {
          const generalSectionKey = `${pageName}.${row.name}.general`;
          const attachmentKey = `${generalSectionKey}.${type}`;
          const typeKey = `${generalSectionKey}.type`;

          saveFieldValue(typeKey, 'general');

          if (isFileAlreadyExists && currentAttachmentSectionInfo?.expectedAttachmentName) {
            const prevAttachmentKey = `${generalSectionKey}.${currentAttachmentSectionInfo.expectedAttachmentName}`;
            const prevFilesArray = getFieldValue(prevAttachmentKey) || [];
            const updatedPrevFilesArray = prevFilesArray.filter((fileName: string) => fileName !== currentAttachment.name);
            if (!updatedPrevFilesArray?.length) {
              unregisterField(prevAttachmentKey);
            } else {
              saveFieldValue(prevAttachmentKey, updatedPrevFilesArray);
            }
          }

          const attachmentNames = getFieldValue(attachmentKey) || [];
          if (!attachmentNames.includes(currentAttachment.name)) {
            saveFieldValue(attachmentKey, [...attachmentNames, currentAttachment.name]);
          }
        }
      }

      closeModalPage();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
      console.error(error);
    } finally {
      setUploadFilesLoading(false);
    }
  };

  const onSubmit = async (data: IFromValues) => {
    if (currentAttachment) {
      updateAttachmentMetadata(data);
      return;
    }
    saveAttachment(file, data);
  };

  return (
    <>
      {modalWindow.isOpen && modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ isOpen: false, type: '' })} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={closeModalPage} />} />
        <PageTitle title="Add An Attachment File" />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <PaddingWrap>
          <AddDocumentForm id="addAnAttachmentFileForm" onSubmit={handleSubmit(onSubmit)}>
            <AddDocumentWrapper isMobile={isMobile}>
              <TwoColumnWrapper isMobile={isMobile}>
                <Controller
                  name="type"
                  control={control}
                  rules={{
                    required: {
                      value: true,
                      message: 'Type is required'
                    }
                  }}
                  render={({ field: { onChange, value }, fieldState: { error } }) => (
                    <Select
                      label="Type"
                      value={value || ''}
                      setValue={(value) => {
                        onChange(value);

                        if (!currentAttachmentSectionInfo?.sectionName || currentAttachmentSectionInfo?.sectionName !== 'general') {
                          const isContactAttachmentsType = contactAttachmentsTypes.some((item) => item.value === value);
                          setShowAssociatedContactSelect(isContactAttachmentsType);
                        }
                      }}
                      options={[{ label: 'Select', value: '' }, ...attachmentTypesOptions]}
                      size="md"
                      errorText={error?.message}
                      isRequiredField
                    />
                  )}
                />
                {showAssociatedContactSelect && (
                  <Controller
                    name="associatedContact"
                    control={control}
                    rules={{
                      required: {
                        value: true,
                        message: 'Associated Contact is required'
                      }
                    }}
                    render={({ field: { onChange, value }, fieldState: { error } }) => (
                      <Select
                        label="Associated Contact"
                        value={value || ''}
                        setValue={onChange}
                        options={[{ label: 'Select', value: '' }, ...associatedContactOptions]}
                        size="md"
                        errorText={error?.message}
                        isRequiredField
                      />
                    )}
                  />
                )}
              </TwoColumnWrapper>
              <Controller
                name="description"
                control={control}
                rules={{
                  maxLength: {
                    value: 250,
                    message: '250 Characters Max'
                  }
                }}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <Input
                    label="Description (250 Characters Max)"
                    value={value || ''}
                    onChange={(e) => {
                      onChange(e.target.value);
                    }}
                    size="md"
                    errorText={error?.message}
                  />
                )}
              />
              {currentAttachment ? (
                <FileUpload fileName={currentAttachment.name} openFile={() => openDocumentationAttachmentFile(currentAttachment.name, entityId)} />
              ) : (
                <>
                  {file ? (
                    <FileUpload
                      type={file.type.includes('image') ? 'image' : 'document'}
                      imageSrc={URL.createObjectURL(file)}
                      fileName={file.name}
                      openFile={() => window.open(URL.createObjectURL(file), '_blank')}
                    />
                  ) : (
                    <DropFileArea
                      id="add-documentation-attachment"
                      handleUploadFile={handleUploadFile}
                      onDropHandler={onDropHandler}
                      drag={drag}
                      setDrag={setDrag}
                      maxSize={10}
                      fileExtensions={fileExtensions}
                    />
                  )}
                </>
              )}
            </AddDocumentWrapper>
            <FormFooter
              onCancel={closeModalPage}
              disableSaveButton={(!file && !currentAttachment) || !!Object.keys(errors || {}).length || uploadFilesLoading}
              isLoading={uploadFilesLoading}
            />
          </AddDocumentForm>
        </PaddingWrap>
      </MainWrap>
    </>
  );
};

const AddDocumentForm = styled.form`
  margin-top: 50px;
`;

const AddDocumentWrapper = styled.div<{ isMobile: boolean }>`
  padding: ${({ isMobile }) => (isMobile ? '20px' : '40px')};
  border-radius: 10px;
  border: 1px solid ${themeVar('border.base')};
  background-color: ${themeVar('layer.1')};
  display: flex;
  flex-direction: column;
  gap: ${({ isMobile }) => (isMobile ? '15px' : '25px')};
`;

const TwoColumnWrapper = styled.div<{ isMobile: boolean }>`
  display: grid;
  grid-template-columns: ${({ isMobile }) => (isMobile ? '1fr' : '1fr 1fr')};
  gap: ${({ isMobile }) => (isMobile ? '15px' : '30px')};
`;

const FilesWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
